import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import { Country, type ICountry, type IState, State } from 'country-state-city';
import { useSnackbar } from 'notistack';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';
import { LoadingProgress } from '../../../layout';
import { BillingInformation } from '../../../models';
import { formatErrorTrackingMethod } from '../../../pages/checkout/utils';
import { useReadOnlyMode } from '../../../routes';
import { fromAccounts, fromAccountUsers, fromAuth, fromBillingInformation, useAppDispatch } from '../../../store';
import { useGetTrackingCookies, usePlanCartSnapshot } from '../../../hook';
import { sendTrackingData } from '../../../utils/utils';
import { CountrySelector, StateSelector } from './CountriesSelection';
import { withCartSnapshot } from '../../../utils/analytics';
import { track } from '../../../analytics';

type BillingFormData = Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>;
const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: 500,
    fontSize: 14,
    marginBottom: theme.spacing(1),
    color: '#1D3066',
  },
  requiredAsterisk: {
    color: '#0f8461',
    fontWeight: 700,
  },
  description: {
    fontSize: theme.spacing(1.5),
    color: '#62839F',
  },
  warning: {
    fontSize: theme.spacing(1.5),
    fontStyle: 'italic',
    color: 'red',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  informationText: {
    marginBottom: theme.spacing(3),
  },
  getQuoteSubtitle: {
    marginLeft: theme.spacing(1),
    fontSize: theme.spacing(1.75),
  },
  getQuoteTitle: {
    fontSize: theme.spacing(2.5),
    display: 'inline',
  },
  getQuoteTitleContainer: {
    borderBottom: '1px solid #D5D8DD',
    paddingBottom: theme.spacing(2),
  },
  cancelBtn: {
    marginRight: theme.spacing(1),
    fontWeight: 500,
    height: 42,
    float: 'right',
    color: '#000000',
    borderRadius: 'border-radius/border-radius-xs',
    borderWidth: '2px',
  },
  saveBtn: {
    fontWeight: 500,
    height: 42,
    minWidth: 72,
    float: 'right',
    color: '#fff',
    backgroundColor: '#5959EB',
    '&:hover': {
      backgroundColor: '#43509B',
    },
  },
  button: {
    fontWeight: 500,
    height: 42,
    minWidth: 72,
    float: 'right',
    color: '#000000',
    borderRadius: 'border-radius/border-radius-xs',
    borderWidth: '2px',
  },
  billingInformationText: {
    fontWeight: 700,
  },
  billingInformationBox: {
    width: '100%',
    padding: theme.spacing(1, 0, 1, 0),
  },
}));

const inputs: Array<keyof Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>> = [
  'fullBusinessName',
  'ccEmail',
  'country',
  'state',
  'city',
  'address1',
  'address2',
  'postalCode',
  'vatNumber',
];

const fieldTrackingInfoMapper: { [key: string]: any } = {
  ccEmail: {
    name: 'cc_email',
    isRequired: false,
  },
  address1: {
    name: 'address_1',
    isRequired: true,
  },
  address2:
    {
      name: 'address_2',
      isRequired: true,
    },
  city: {
    name: 'city',
    isRequired: true,
  },
  state: {
    name: 'state',
    isRequired: true,
  },
  country: {
    name: 'country',
    isRequired: true,
  },
  postalCode: {
    name: 'postal_code',
    isRequired: true,
  },
  vatNumber: {
    name: 'vat_gsc_id',
    isRequired: true,
  },
  fullBusinessName: {
    name: 'business_name',
    isRequired: true,
  },
};

interface BillingInformationComponentProps {
  accountId: number;
  enableSave: boolean;
  currentBillingInformation?: BillingInformation;
  /**
   * Payment & billing v1 redesign: always-editable form without the
   * Update/Save/Cancel buttons — saving happens via validateAndSave() when the
   * page-level "Continue to payment" button is clicked.
   */
  redesign?: boolean;
  /**
   * Payment & billing page redesign: the section owns the "Billing
   * information" header (title + Edit/Cancel links), so skip the form's
   * built-in title and description.
   */
  hideHeader?: boolean;
}

export const BillingInformationComponent = forwardRef((
  props: BillingInformationComponentProps,
  ref,
) => {
  const classes = useStyles();
  const {
    accountId,
    currentBillingInformation,
    enableSave,
    redesign,
    hideHeader,
  } = props;
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const formRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const isEditBillingInfo = useSelector(fromBillingInformation.isEditBillingInfo);
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));
  const user = useSelector(fromAuth.selectUser);
  const editBillingInfo = () => {
    dispatch(fromBillingInformation.setEditBillingInfo(true));
  };

  useEffect(() => {
    if (!isEditBillingInfo) {
      dispatch(fromBillingInformation.setBillingInfoValid(true));
    }
  }, [isEditBillingInfo]);
  const isNotSyncedBillingInfo = useSelector(
    fromBillingInformation.isNotSyncedAddressByAccountId(Number(accountId)),
  ) ?? false;
  const accountTotalMembers = useSelector(fromAccountUsers.selectCountByAccountId(
    Number(accountId),
  ));
  const cartSnapshot = usePlanCartSnapshot();

  const currentAccountUser = useSelector(fromAccountUsers.selectOneByUserIdAndAccountId(
    Number(user?.id),
    Number(account?.id),
  ));
  const [loading, setLoading] = useState(false);
  const isReadOnly = useReadOnlyMode();

  useEffect(() => {
    const getBillingInformation = async () => {
      if (accountId) {
        await dispatch(fromBillingInformation.doGetBillingInformation(+accountId));
      }
    };
    getBillingInformation().catch(() => {
    });
  }, [accountId, dispatch]);
  const schema = object().shape({
    ccEmail: string()
      .notRequired()
      .matches(
        /^$|[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.email.invalid' }),
      )
      .email(
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.email.invalid' }),
      ),
    fullBusinessName: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Full Business Name' },
        ),
      ),
    country: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Country' },
        ),
      )
      .matches(
        /^[a-zA-Z,. ]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.country.invalid' }),
      ),
    state: string(),
    city: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'City' },
        ),
      )
      .matches(
        /^[a-zA-Z ,.;'&/()-]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.city.invalid' }),
      ),
    address1: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Address Line 1' },
        ),
      ),
    address2: string(),
    postalCode: string().trim()
      .required(
        intl.formatMessage(
          { id: 'billinginfo.billingcontact.error.required' },
          { field: 'Postal Code' },
        ),
      )
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.postalCode.invalid' }),
      ),
    vatNumber: string()
      .matches(
        /^[a-zA-Z0-9 .-]*$/,
        intl.formatMessage({ id: 'billinginfo.billingcontact.error.vatId.invalid' }),
      ),
  });

  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
  const [selectedState, setSelectedState] = useState<IState | null>(null);
  const trackingCookies = useGetTrackingCookies();

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    clearErrors,
    trigger,
    reset,
  } = useForm<Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>>({
    mode: 'onChange',
    defaultValues: currentBillingInformation ?? {
      ccEmail: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      vatNumber: '',
      fullBusinessName: '',
    },
    resolver: yupResolver(schema),
  });

  const mapStateDataToInputs = () => {
    dispatch(fromBillingInformation.setEditBillingInfo(
      !currentBillingInformation || isNotSyncedBillingInfo,
    ));
    if (currentBillingInformation) {
      inputs.forEach(input => {
        if (currentBillingInformation[input]) {
          setValue(
            input,
            currentBillingInformation[input].toString(),
          );
        } else {
          setValue(
            input,
            '',
          );
        }
      });
    }
  };

  useEffect(() => {
    mapStateDataToInputs();
    if (currentBillingInformation) {
      setSelectedCountry(Country.getAllCountries().find(it => it.isoCode
        === currentBillingInformation.country) || null);
      setSelectedState(State.getStatesOfCountry(currentBillingInformation.country)
        .find(it => it.isoCode === currentBillingInformation.state
          || it.name === currentBillingInformation.state) ?? null);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [currentBillingInformation, setValue]);

  const sendInvalidSaveButtonTracking = (errors: string | undefined | string[]) => {
    const additionalInfos = withCartSnapshot({
      field: 'billing_information',
      object: 'button',
      action: 'checkout_page_information_saved',
      is_required: true,
      is_valid: false,
      error: errors,
      ...trackingCookies,
    }, cartSnapshot);
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      additionalInfos,
    );
  };

  const sendValidSaveButtonTracking = () => {
    const additionalInfos = withCartSnapshot({
      field: 'billing_information',
      object: 'button',
      action: 'checkout_page_information_saved',
      is_required: true,
      is_valid: true,
      ...trackingCookies,
    }, cartSnapshot);
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      additionalInfos,
    );
  };

  const onSubmitFormError = (fieldErrors: FieldErrors) => {
    if (fieldErrors) {
      const convertedErrors: string[] = Object.keys(errors)
        .map(field => {
          const errorType = (fieldErrors[(field as keyof BillingFormData)]?.type) as string;
          const fieldInfo = fieldTrackingInfoMapper[field];
          return formatErrorTrackingMethod(fieldInfo?.name, errorType);
        });
      sendInvalidSaveButtonTracking(convertedErrors);
    }
  };

  const onBlurredField = (event: React.FocusEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as keyof BillingFormData;
    trigger(fieldName).then(() => {
      const fieldState = control.getFieldState(fieldName);
      const trackingFieldInfo = fieldTrackingInfoMapper[fieldName];
      let additionalInfos;
      dispatch(fromBillingInformation.setBillingInfoValid(!fieldState.error));
      if (fieldState.error) {
        // Invalid tracking infor
        const isContentDeleted = !event.target.value;
        // Field fullBusinessName - consider delete event
        if (fieldName === 'fullBusinessName' && isContentDeleted) {
          additionalInfos = {
            field: 'billing_information',
            object: trackingFieldInfo.name,
            action: 'delete',
            is_required: trackingFieldInfo.isRequired,
            is_valid: false,
            error: formatErrorTrackingMethod(trackingFieldInfo.name, fieldState.error.type),
          };
        } else {
          additionalInfos = {
            field: 'billing_information',
            object: trackingFieldInfo.name,
            action: 'fill',
            is_required: trackingFieldInfo.isRequired,
            is_valid: false,
            error: formatErrorTrackingMethod(trackingFieldInfo.name, fieldState.error.type),
          };
        }
      } else {
        // Valid tracking infor
        additionalInfos = {
          field: 'billing_information',
          object: trackingFieldInfo.name,
          action: 'fill',
          is_required: trackingFieldInfo.isRequired,
          is_valid: true,
          ...trackingCookies,
        };
      }

      sendTrackingData(
        'checkout_page_all_action',
        accountId,
        undefined,
        withCartSnapshot(additionalInfos, cartSnapshot),
      );
    });
  };

  const renderView = ({ mode, inputKey, field, intl, errors, onBlurredField }: any) => {
    const isView = mode === 'view';

    if (inputKey !== 'country' && inputKey !== 'state') {
      return (
        <TextField
          id={inputKey}
          {...field}
          variant="outlined"
          fullWidth
          placeholder={intl.formatMessage({
            id: `billinginfo.billingcontact.placeholder.${inputKey}`,
          })}
          size="small"
          error={!!errors[inputKey]}
          helperText={errors[inputKey]?.message}
          onBlur={onBlurredField}
          fontSize={16}
          disabled={isView}
        />
      );
    }
    if (inputKey === 'state') {
      return (
        <StateSelector
          selectedCountry={selectedCountry}
          selectedState={selectedState}
          setSelectedState={(state: IState) => {
            setSelectedState(state);
          }}
          field={field}
          errors={errors}
          inputKey={inputKey}
          disabled={isView}
        />
      );
    }
    return (
      <CountrySelector
        selectedCountry={selectedCountry}
        setSelectedCountry={(country: ICountry) => {
          setSelectedCountry(country);
          setSelectedState(null);
        }}
        field={field}
        errors={errors}
        inputKey={inputKey}
        disabled={isView}
      />
    );
  };

  const formBillingInformation = () => (
    <form
      id="billing-information-form"
      ref={formRef}
      onSubmit={handleSubmit(submitBillingContact, onSubmitFormError)}
    >
      {!hideHeader && (
      <Grid container item>
        <Grid item xs={8}>
          <Typography variant="h3" className={classes.billingInformationText}>
            {intl.formatMessage({ id: 'billing.information' })}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          {enableSave && !redesign && (
            <Button
              variant="contained"
              color="primary"
              className={classes.saveBtn}
              type="submit"
              disabled={loading || isReadOnly}
            >
              {intl.formatMessage({ id: 'billinginfo.button.save' })}
            </Button>
          )}
          {!!currentBillingInformation && !isNotSyncedBillingInfo && !redesign && (
            <Button
              variant="outlined"
              color="inherit"
              className={classes.cancelBtn}
              onClick={handleCancel}
              disabled={loading || isReadOnly}
            >
              {intl.formatMessage({ id: 'billinginfo.button.cancel' })}
            </Button>
          )}
        </Grid>
      </Grid>
      )}
      {!hideHeader && (
      <Typography variant="subtitle2" className={classes.description}>
        {intl.formatMessage({ id: 'billinginfo.billingcontact.description' })}
      </Typography>
      )}
      {enableSave && !currentBillingInformation && !redesign && (
        <Typography variant="subtitle2" className={classes.warning}>
          {intl.formatMessage({ id: 'billinginfo.billingcontact.warning' }, { isGetQuoteUrl: false })}
        </Typography>
      )}
      {renderBillingInfoLayout('edit')}
      {!hideHeader && (
        <>
          <br />
          <Grid container>
            <Grid item xs={3} md={2} lg={1} />
          </Grid>
        </>
      )}
      {loading && <LoadingProgress fullscreen />}
    </form>
  );

  const renderBillingInfoLayout = (mode: 'view' | 'edit') => {
    const renderItem = (inputKey: keyof BillingInformation) => renderBillingItem(inputKey, mode);

    return (
      <Box
        id="billing-information-form-layout"
        display="flex"
        flexDirection="column"
        paddingY={2}
      >
        <Box
          id="billing-information-form-layout-fullBusinessName-ccEmail"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          className={classes.billingInformationBox}
        >
          <Box
            id="billing-information-form-layout-fullBusinessName"
            display="flex"
            flexDirection="column"
            width="50%"
            marginRight={2}
          >
            {/* Full Business Name */}
            {renderItem('fullBusinessName')}
          </Box>
          <Box
            id="billing-information-form-layout-ccEmail"
            display="flex"
            flexDirection="column"
            width="50%"
            marginLeft={2}
          >
            {/* CC Email */}
            {renderItem('ccEmail')}
          </Box>
        </Box>
        <Box
          id="billing-information-form-layout-country-state"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          className={classes.billingInformationBox}
        >
          <Box
            id="billing-information-form-layout-country"
            display="flex"
            flexDirection="column"
            width="50%"
            marginRight={2}
          >
            {/* Country */}
            {renderItem('country')}
          </Box>
          <Box
            id="billing-information-form-layout-state"
            display="flex"
            flexDirection="column"
            width="50%"
            marginLeft={2}
          >
            {/* State */}
            {renderItem('state')}
          </Box>
        </Box>
        <Box
          id="billing-information-form-layout-city-postalCode"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          className={classes.billingInformationBox}
        >
          <Box
            id="billing-information-form-layout-city"
            display="flex"
            flexDirection="column"
            width="50%"
            marginRight={2}
          >
            {/* City */}
            {renderItem('city')}
          </Box>
          <Box
            id="billing-information-form-layout-postalCode"
            display="flex"
            flexDirection="column"
            width="50%"
            marginLeft={2}
          >
            {/* Postal Code */}
            {renderItem('postalCode')}
          </Box>
        </Box>
        <Box
          id="billing-information-form-layout-address1"
          display="flex"
          flexDirection="column"
          className={classes.billingInformationBox}
        >
          {/* Address 1 */}
          {renderItem('address1')}
        </Box>
        <Box
          id="billing-information-form-layout-address2"
          display="flex"
          flexDirection="column"
          className={classes.billingInformationBox}
        >
          {/* Address 2 */}
          {renderItem('address2')}
        </Box>
        <Box
          id="billing-information-form-layout-vat"
          display="flex"
          flexDirection="column"
          className={classes.billingInformationBox}
        >
          {/* VAT Number */}
          {renderItem('vatNumber')}
        </Box>
      </Box>
    );
  };

  const saveBillingContact = async (formInputs: BillingFormData): Promise<boolean> => {
    setLoading(true);
    return dispatch(
      fromBillingInformation.doCreateBillingInformation({
        accountId: Number(accountId),
        ...formInputs,
      }),
    )
      .then(unwrapResult)
      .then(() => {
        enqueueSnackbar(
          <FormattedMessage id="billinginfo.billingcontact.update.success" />,
          { variant: 'success' },
        );
        track(
          'Billing Information Saved',
          {
            id: user?.id,
            user_id: user?.email,
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            system_role: user?.roles,
            org_role: currentAccountUser?.role,
            org_id: account?.id,
            org_name: account?.name,
            total_members: accountTotalMembers,
          },
        );
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          user_id: user?.id,
          org_role: currentAccountUser?.role,
          system_role: user?.roles,
          org_id: account?.id,
          org_name: account?.name,
          total_members: accountTotalMembers,
          event: 'gtm_billing_information_saved',
        });
        sendValidSaveButtonTracking();
        return true;
      })
      .catch((ex: any) => {
        mapStateDataToInputs();
        sendInvalidSaveButtonTracking(ex.message);
        return false;
      })
      .finally(() => {
        dispatch(fromBillingInformation.setEditBillingInfo(false));
        setLoading(false);
      });
  };

  const submitBillingContact = (
    inputs: Required<Omit<BillingInformation, 'id' | 'createdAt' | 'updatedAt' | 'accountId' | 'archived'>>,
  ) => {
    saveBillingContact(inputs).catch(() => {
    });
  };

  const handleCancel = () => {
    clearErrors();
    reset(currentBillingInformation, { keepDefaultValues: true });
    dispatch(fromBillingInformation.setEditBillingInfo(false));
  };

  const hasBillingChanges = (formInputs: BillingFormData) => !currentBillingInformation
    || isNotSyncedBillingInfo
    || inputs.some(key => (
      (currentBillingInformation[key] ?? '').toString() !== (formInputs[key] ?? '')
    ));

  useImperativeHandle(ref, () => ({
    saveBillingInformation: () => {
      formRef.current?.requestSubmit();
    },
    /**
     * Validates the form; saves only when something changed (or the stored
     * address is out of sync). Resolves true when checkout can proceed.
     */
    validateAndSave: () => new Promise<boolean>(resolve => {
      handleSubmit(
        formInputs => {
          if (!hasBillingChanges(formInputs)) {
            resolve(true);
            return;
          }
          saveBillingContact(formInputs)
            .then(resolve)
            .catch(() => resolve(false));
        },
        fieldErrors => {
          onSubmitFormError(fieldErrors);
          resolve(false);
        },
      )();
    }),
  }));

  const renderBillingItem = (inputKey: any, mode: string) => {
    const label = intl.formatMessage({ id: `billinginfo.billingcontact.${inputKey}` });
    // Redesign mock renders the required marker as a separate green " *"
    // instead of the asterisk baked into the translation string.
    const isRequired = label.endsWith('*');
    return (
      <>
        <Typography
          variant="body1"
          className={classes.label}
        >
          {redesign && isRequired
            ? (
              <>
                {label.slice(0, -1).trim()}
                <span className={classes.requiredAsterisk}> *</span>
              </>
            )
            : label}
        </Typography>
        <Controller
          render={({ field }) => renderView({
            mode, inputKey, field, intl, errors, onBlurredField,
          })}
          name={inputKey}
          control={control}
        />
      </>
    );
  };

  const isEditMode = redesign || isEditBillingInfo;

  return (
    <Box id="billing-information" paddingTop={3}>
      {!isEditMode && (
        <Grid container>
          <Grid container item>
            <Grid item xs={8}>
              <Typography variant="h6" className={classes.billingInformationText}>
                <FormattedMessage id="billing.information" />
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Button
                id="biling-info-update-btn"
                variant="outlined"
                className={classes.button}
                onClick={editBillingInfo}
                disabled={loading || isReadOnly}
              >
                <FormattedMessage id="billinginfo.button.update" />
              </Button>
            </Grid>
          </Grid>
          <Grid container item>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className={classes.description}>
                {intl.formatMessage({ id: 'billinginfo.billingcontact.description' })}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {!currentBillingInformation && (
                <Typography variant="subtitle2" className={classes.warning}>
                  {intl.formatMessage({ id: 'billinginfo.billingcontact.warning' }, { isGetQuoteUrl: false })}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      {isEditMode
        ? formBillingInformation() : renderBillingInfoLayout('view')}
    </Box>
  );
});
